import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders, HttpResponse} from '@angular/common/http';
import {Observable, tap} from 'rxjs';
import {DokumentModels} from '../models/dokument-models';
import {HelperService} from './helper.service';
import {SwissAppConfigModels} from '../models/swissappconfig-models';
import {ObjektModels} from '../models/objekt-models';
import {ServiceAppService} from './service-app.service';
import {ServiceAppModels} from '../models/serviceapp-models';
import {LocalStorageService} from '../local-storage.service';
import {NavigationEnd, Router} from '@angular/router';
import {filter, map} from 'rxjs/operators';
import {ObjektDataService} from './objekt-data.service';
import {UserAuthenticationService} from './user-authentication.service';
import {MatDialog} from '@angular/material/dialog';
import {PDFViewerDialogComponent} from '../pdfviewer/pdfviewerDialog.component';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {v4 as uuidv4} from 'uuid';
import NotizblattDokument = DokumentModels.NotizblattDokument;
import DokumentInfo = DokumentModels.DokumentInfo;
import DocumentInfoWithFile = DokumentModels.DocumentInfoWithFile;
import DokumentSource = DokumentModels.DokumentSource;
import DokumentTyp = DokumentModels.DokumentTyp;

@Injectable({
    providedIn: 'root',
})
export class DokumentDataService {
    private webServiceConfig?: SwissAppConfigModels.WebServiceConfig;
    private previousUrl = '';
    private currentUrl = '';

    /* Offline-Daten aus Navi */
    private storageWebMain = 'SwissWebMainDB';
    private dbStorageSA_Doku = 'ServiceAppDB_Doku';
    private dbObjektAktivitaetenFiles: ObjektModels.ObjektAktivitaetDatei[] = [];
    private dbObjektAktivitaeten: ObjektModels.ObjektAktivitaetListElement[] = [];
    private dbNotizBlaetter: DokumentModels.NotizblattDokument[] = [];
    private sideBarElements: DokumentInfo[] = [];
    /* Ende Offline-Daten aus Navi */

    // TODO: 2025-07-29 EN-3729 DokumentService should not keep track of DokumentInfo
    private dokuInfo: DokumentModels.DokumentInfo = {
        objectID: '', documentID: '', fileName: '', description: '', dokumentTyp: DokumentModels.DokumentTyp.Dokument, dokumentQuelle: DokumentModels.DokumentSource.FromWebService, lastModified: new Date()
    };

    constructor(public dialog: MatDialog, private http: HttpClient, private userAuthService: UserAuthenticationService, private helperService: HelperService, private localStorageService: LocalStorageService, private svcObjekte: ObjektDataService, private svcSA: ServiceAppService, private router: Router) {
        this.helperService.getSwissWebServiceConfig$().pipe(
            takeUntilDestroyed(),
            tap((config) => {
                this.webServiceConfig = config;
            })
        ).subscribe();

        this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe({
                next: (event: any) => {
                    this.previousUrl = this.currentUrl;
                    this.currentUrl = event.url;
                },
            });
    }

    /* Hier wird gespeichert, welches Dokument das im PDF-Viewer geöffnet werden muss */
    setPDFDokument(objectID: string, documentID: string, filename: string, description: string, dokumentTyp: DokumentModels.DokumentTyp = DokumentModels.DokumentTyp.Dokument,
                   dokumentQuelle: DokumentModels.DokumentSource = DokumentModels.DokumentSource.FromWebService, lastModified: Date | undefined = undefined, sidebarElements: DokumentInfo[] = []): void {
        this.dokuInfo.objectID = objectID;
        this.dokuInfo.documentID = documentID;
        this.dokuInfo.fileName = filename;
        this.dokuInfo.description = description;
        this.dokuInfo.dokumentTyp = dokumentTyp;
        this.dokuInfo.dokumentQuelle = dokumentQuelle;
        this.dokuInfo.lastModified = lastModified || new Date();
        if (!lastModified) {
            console.error('LastModified was undefined');
        }
        this.sideBarElements = sidebarElements;
    }

    // TODO: 2025-07-29 EN-3729 DokumentService should not keep track of DokumentInfo
    setDocumentInfo(dokuInfo: DokumentModels.DokumentInfo): void {
        this.dokuInfo = dokuInfo;
    }

    // TODO: 2025-07-29 EN-3729 DokumentService should not keep track of DokumentInfo
    getDocumentInfo(): DokumentModels.DokumentInfo {
        return this.dokuInfo;
    }

    getDocument(documentID: string, checkOffline = false): Promise<DocumentInfoWithFile> {
        const documentInfo = this.dokuInfo;
        documentInfo.documentID = documentID;
        return new Promise(async (resolve, reject) => {
            if (!this.webServiceConfig) {
                reject();
                return;
            }
            const url = this.webServiceConfig.dokuUrl;

            try {
                if (!checkOffline && (documentInfo.dokumentQuelle === DokumentModels.DokumentSource.FromWebService || this.helperService.isWebServiceAvailable())) {
                    const headers = new HttpHeaders({
                        Authorization: this.helperService.getSwissAuthorization(this.userAuthService.currentUserInfo),
                    });
                    this.http.get<any>(url + '/DownloadDatei?id=' + documentInfo.documentID + '&loginname=' + this.userAuthService.getCurrentAuthenticatedUser()?.loginname, {headers, responseType: 'blob' as 'json', observe: 'response'}).subscribe({
                        next: (res) => {
                            const binaryData: BlobPart[] = [];
                            binaryData.push(res.body as BlobPart);
                            const document = new DocumentInfoWithFile(documentInfo, new File(binaryData, documentInfo.fileName));
                            const baseDate = res.headers.get('X-Last-Modified');
                            document.lastModified = baseDate ? new Date(baseDate) : new Date();
                            document.isS3Document = res.headers.get('X-Is-S3-Document') === 'true';
                            resolve(document);
                        }, error: async (err: any) => {
                            const res = await this.getDocument(documentID, true);
                            res.fromLocalStorage = true;
                            resolve(res);
                        }
                    });
                } else {
                    const offlineActivities = await this.localStorageService.get('offlineObjektAktivitaetenDateien', 'SwissWebMainDB', false, []);
                    const activity: ObjektModels.ObjektAktivitaetDatei = offlineActivities.find(oA => oA.documentID === documentInfo.documentID);
                    if (activity) {
                        const document = new DocumentInfoWithFile(documentInfo, new File([activity.binary], activity.fileName));
                        document.fromLocalStorage = true;
                        resolve(document);
                    } else {
                        const alt = await this.getPDFDokumentFromIndexDBLegacyFallback(documentInfo);
                        const document = new DocumentInfoWithFile(documentInfo, new File([alt], documentInfo.fileName));
                        document.fromLocalStorage = true;
                        resolve(document);
                    }
                }
            } catch (ex) {
                reject();
            }
        });
    }

    public getSidebarElements(): DokumentInfo[] {
        return this.sideBarElements;
    }

    async getPDFDokumentFromIndexDBLegacyFallback(documentInfo: { documentID: string, dokumentQuelle: DokumentModels.DokumentSource, dokumentTyp: DokumentModels.DokumentTyp } | undefined): Promise<Blob> {
        documentInfo = documentInfo || this.dokuInfo;
        try {
            switch (documentInfo.dokumentQuelle) {
                case DokumentModels.DokumentSource.SA_DBChecklisten:
                    return (this.svcSA.getChecklisteByDocumentID(documentInfo.documentID).binary);
                case DokumentModels.DokumentSource.ObjektReport:
                    return (this.svcObjekte.getObjektReportByDocumentID(documentInfo.documentID).binary);
                case DokumentModels.DokumentSource.SA_DBDokumente:
                    const res = await this.svcSA.getFileByDocumentID(documentInfo.documentID, DokumentModels.DokumentSource.SA_DBDokumente);
                    if (res !== undefined) {
                        return (res.binary);
                    }
                    return (new Blob());
                case DokumentModels.DokumentSource.OfflineObjektNavi:
                    return (this.getFileByDocumentID(documentInfo.dokumentTyp, documentInfo.documentID).binary);
                case DokumentModels.DokumentSource.SA_DBBilder:
                    return (this.svcSA.getBildByDocumentID(documentInfo.documentID).binary);
                case DokumentSource.SA_DBRapporte:
                    return (this.svcSA.getRapporteByDocumentID(documentInfo.documentID).binary);
                case DokumentSource.ObjektRapport:
                default:
                    return (this.svcObjekte.getRapporteByDocumentID(documentInfo.documentID).binary);
            }
        } catch (ex) {
            return new Blob();
        }
    }

    downloadDocument(documentID: string, filename: string): void {
        const headers = new HttpHeaders({
            Authorization: this.helperService.getSwissAuthorization(this.userAuthService.currentUserInfo),
        });

        const url = this.webServiceConfig!.dokuUrl;
        this.http.get<any>(url + '/DownloadDatei?id=' + documentID + '&loginname=' + this.userAuthService.getCurrentAuthenticatedUser()?.loginname, {headers, responseType: 'blob' as 'json'}).subscribe({
            next: (response) => {
                const dataType = response.type;
                const binaryData: any[] = [];
                binaryData.push(response);
                const blob = new Blob(binaryData, {type: dataType});
                HelperService.downLoadFile(blob, filename);
            },
        });
    }

    async getDocumentBlob(documentID: string): Promise<Blob> {
        const headers = new HttpHeaders({
            Authorization: this.helperService.getSwissAuthorization(this.userAuthService.currentUserInfo),
        });

        const url = this.webServiceConfig!.dokuUrl;
        return new Promise(resolve => {
            this.http.get<any>(url + '/DownloadDatei?id=' + documentID + '&loginname=' + this.userAuthService.getCurrentAuthenticatedUser()?.loginname, {headers, responseType: 'blob' as 'json'}).subscribe({
                next: (response) => {
                    const dataType = response.type;
                    const binaryData: any[] = [];
                    binaryData.push(response);
                    resolve(new Blob(binaryData, {type: dataType}));
                },
            });
        });
    }


    getOfflineOtherDokument(fileBlob: Blob, filename: string): void {
        const downloadLink = document.createElement('a');
        downloadLink.href = window.URL.createObjectURL(fileBlob);
        if (filename) {
            downloadLink.setAttribute('download', filename);
        }
        document.body.appendChild(downloadLink);
        downloadLink.click();
    }

    getOtherDokumentFile(documentID: string, filename: string): Promise<Blob | undefined> {
        return new Promise((resolve) => {
            const headers = new HttpHeaders({
                Authorization: this.helperService.getSwissAuthorization(this.userAuthService.currentUserInfo),
            });

            const url = this.webServiceConfig!.dokuUrl;
            try {
                this.http.get<any>(url + '/DownloadDatei?id=' + documentID + '&loginname=' + this.userAuthService.getCurrentAuthenticatedUser()?.loginname, {headers, responseType: 'blob' as 'json'})
                    .subscribe({
                        next: (response) => {
                            const dataType = response.type;
                            const binaryData: any[] = [];
                            binaryData.push(response);
                            resolve(new Blob(binaryData, {type: dataType}));
                        }, error: () => resolve(undefined),
                    });
            } catch (ex) {
                console.error('Fehler beim Download...');
            }
        });
    }

    getImageFromIndexedDB(documentID: string, filename: string): Blob {
        const bild: ServiceAppModels.SA_BildFile = this.svcSA.getBildByDocumentID(documentID);
        if (bild) {
            return bild.binary;
        } else {
            return new Blob();
        }
    }

    public uploadDokumentWithInfo(file: File, dokuInfo: DokumentModels.DokumentInfo = this.dokuInfo): Observable<{ value: string; fileName: string; timestamp: string }> {
        if (!this.webServiceConfig) {
            throw new Error();
        }

        if (dokuInfo.isS3Document) {
            // if document is based on a .S3 document we need a new ID to save it
            this.dokuInfo.documentID = uuidv4();
        }

        const formData = new FormData();
        formData.append('fileName', dokuInfo.fileName);
        formData.append('description', dokuInfo.description);
        formData.append('documentType', dokuInfo.dokumentTyp);
        formData.append('objectID', dokuInfo.objectID);
        formData.append('documentID', dokuInfo.documentID);

        formData.append('concurrencyCheck', (dokuInfo.lastModified || new Date()).toISOString());
        formData.append('file', file);

        const url = this.webServiceConfig.dokuUrl;

        type ProblemDetails = { title: string; details: string; status: number } & { [key: string]: unknown };
        type ProblemDetailsResponse<T> = ({ ok: true } & HttpResponse<T>) | ({ ok: false } & HttpResponse<ProblemDetails>);
        type UploadDokuResponse = { value: string; fileName: string; timestamp: string };

        return this.http.post<UploadDokuResponse>(url + '/UploadDoku', formData, {observe: 'response'}).pipe(
            map(r => r as ProblemDetailsResponse<UploadDokuResponse>),
            tap(r => console.log(`Is success: ${r.ok}`)),
            tap(r => console.log(`Is concurrency conflict: ${r.status === 409}`)),
            tap(r => console.log(`New file name: ${r.ok ? r.body?.fileName : 'error'}`)),
            tap(r => console.log(`New timestamp: ${r.body?.timestamp}`)), // timestamp is a custom extension in the error problems and is always returned (see: ExceptionHandler.cs in backend)
            tap(r => {
                if (!r.ok && r.status !== 409) {
                    throw new Error(r.body?.title); // { observe: 'response' } does not throw errors when an error status code is returned
                }
            }),
            map(r => r.body as UploadDokuResponse)
        );
    }

    public uploadBild(bildInfo: DokumentModels.DokumentBild): Observable<Response> {
        if (!this.webServiceConfig) {
            throw new Error();
        }

        const formData = new FormData();
        formData.append('fileName', bildInfo.fileName);
        formData.append('description', bildInfo.fileName.split('.')[0]);
        formData.append('documentType', '9');
        formData.append('objectID', bildInfo.objectID);
        formData.append('documentID', bildInfo.documentID);
        formData.append('concurrencyCheck', (bildInfo.lastModified || new Date()).toISOString()); // TODO get timestamp from somewhere, make sure to include timezone
        formData.append('file', bildInfo.binary);

        const url = this.webServiceConfig.dokuUrl;
        return this.http.post<Response>(url + '/UploadDoku', formData);
    }

    async updateNotizblattLocal(noteObj: DokumentModels.NotizblattDokument) {
        const index = this.dbNotizBlaetter.findIndex((x) => x.documentID === noteObj.documentID);
        this.dbNotizBlaetter[index] = noteObj;

        // Indexed-DB aktualisieren
        await this.localStorageService.add('notizblaetter', this.dbNotizBlaetter, this.storageWebMain);
    }

    // Zurückgeliefert wird der neue Dateiname, wenn das Notizblatt erfolgreich erstellt werden konnte
    async saveNotizblatt(documentID: string, objectID: string, description: string, fileName: string, binaryPDF: Blob, hasObjektDokus: number): Promise<DokumentInfo> {
        const info: DokumentModels.DokumentInfo = {
            objectID,
            documentID,
            fileName,
            description,
            lastModified: new Date(),
            dokumentTyp: DokumentModels.DokumentTyp.Notizblatt,
            dokumentQuelle: DokumentModels.DokumentSource.FromWebService,
            userName: this.userAuthService.getCurrentAuthenticatedUser()?.loginname
        };

        await new Promise((resolve, reject) => {
            this.uploadDokumentWithInfo(this.blobToFile(binaryPDF, fileName), info).subscribe({
                next: async (res) => {
                    info.lastModified = new Date(res.timestamp);
                    info.fileName = res.fileName;
                    const saNoteFile: DokumentModels.NotizblattDokument = {
                        objectID: info.objectID,
                        description: info.description,
                        createdBy: this.userAuthService.getCurrentAuthenticatedUser()!.loginname,
                        createdDate: new Date(res.timestamp),
                        documentID: info.documentID,
                        fileName: info.fileName,
                        binary: binaryPDF,
                        lastModified: info.lastModified
                    };
                    this.setDocumentInfo(info);
                    await this.updateNotizblattLocal(saNoteFile);
                    resolve(info);
                }, error: (err) => {
                    reject();
                }
            });
        });
        return info;
    }

    public getDokumenteByObjectID(objectID: string): Observable<DokumentModels.Dokument[]> {
        const url = this.webServiceConfig!.dokuUrl;
        return this.http.get<DokumentModels.Dokument[]>(url + '/ByObject?ObjectID=' + objectID, {
            headers: {
                Authorization: this.helperService.getSwissAuthorization(this.userAuthService.currentUserInfo),
            }

        });
    }

    delLocalNotizblatt(documentID: string) {
        // Wenn das Notizblatt in der Indexed-DB vorhanden ist: löschen
        this.getNotizblattExitsInLocalDB(documentID).then((value) => {
            // Notizblatt existiert im lokalen Speicher
            if (value) {
                const index = this.dbNotizBlaetter.findIndex((x) => (x.documentID = documentID));
                this.dbNotizBlaetter.splice(index, 1);
                this.localStorageService.add('notizblaetter', this.dbNotizBlaetter, this.storageWebMain);
            }
        });
    }

    getFileByDocumentID(dokumentTyp: DokumentModels.DokumentTyp, documentID: string): ObjektModels.ObjektDatei {
        if (dokumentTyp !== DokumentModels.DokumentTyp.Notizblatt) {
            // Zum jetzigen sind die Offline-Dokumente bereits in der Indexed-DB (via openLocalDocument)
            return this.dbObjektAktivitaetenFiles.filter((x) => x.documentID == documentID)[0];
        } else {
            const noteFile = this.dbNotizBlaetter.filter((x) => x.documentID === documentID)[0];
            return {
                documentID: noteFile.documentID, description: noteFile.description, objectID: noteFile.objectID, fileName: noteFile.fileName, binary: noteFile.binary, lastModified: noteFile.lastModified
            };
        }
    }

    async addNotizblattInLocalDB(documentID: string, objectID: string, desciption: string, fileName: string, binaryPDF: Blob, lastModified: Date): Promise<boolean> {
        const saNoteFile: DokumentModels.NotizblattDokument = {
            documentID, description: desciption, createdBy: this.userAuthService.getCurrentAuthenticatedUser()!.loginname, createdDate: new Date(), objectID, fileName, binary: binaryPDF, lastModified
        };
        this.dbNotizBlaetter.push(saNoteFile);
        await this.localStorageService.add('notizblaetter', this.dbNotizBlaetter, this.storageWebMain);
        return true;
    }

    async getNotizblattExitsInLocalDB(documentID: string): Promise<DokumentModels.NotizblattDokument | undefined> {
        const value: NotizblattDokument[] = await this.localStorageService.get('notizblaetter', this.storageWebMain, false, []);
        return value.find((x) => x.documentID === documentID);
    }

    getOfflineObjektAktivitaeten(objectID: string): Observable<ObjektModels.ObjektAktivitaetListElement[]> {
        const url = this.webServiceConfig!.objUrl;
        return this.http.get<ObjektModels.ObjektAktivitaetListElement[]>(url + '/ObjektAktivitaetenTopDown?ObjectID=' + objectID, {
            headers: {
                Authorization: this.helperService.getSwissAuthorization(this.userAuthService.currentUserInfo),
            }
        });
    }

    getOfflineObjectActivities(objectID: string): Promise<ObjektModels.ObjektAktivitaetListElement[]> {
        return new Promise((resolve) => {
            this.localStorageService
                .get('offlineObjektAktivitaeten', this.storageWebMain)
                .then((value) => {
                    if (value !== false) {
                        this.dbObjektAktivitaeten = (value as ObjektModels.ObjektAktivitaetListElement[]);
                        this.dbObjektAktivitaeten = this.dbObjektAktivitaeten.filter((oa) => oa.objectID == objectID);
                        this.dbObjektAktivitaeten = this.dbObjektAktivitaeten.sort((a, b) => {
                            // Sortierung nach Datum, danach nach Beschreibung
                            if (a.datum === b.datum) {
                                return (a.description < b.description && 1) || -1;
                            }
                            return (a.datum < b.datum && 1) || -1;
                        });
                        resolve(this.dbObjektAktivitaeten);
                    }
                });
        });
    }

    /* Speichert die Dokumente eines Objektes und dessen Unterobjekte in die DB */
    saveObjectActivitiesInDB(objectID: string): Promise<boolean> {
        return new Promise((resolve) => {
            this.getOfflineObjektAktivitaeten(objectID).subscribe({
                next: async (res) => {
                    const objekteAktivitaeten: ObjektModels.ObjektAktivitaetListElement[] = res;

                    if (objekteAktivitaeten.length > 0) {
                        const value = await this.localStorageService.get('offlineObjektAktivitaeten', this.storageWebMain);
                        if (value === false) {
                            this.dbObjektAktivitaeten = [];
                            this.dbObjektAktivitaetenFiles = [];
                            this.dbObjektAktivitaeten = this.dbObjektAktivitaeten.concat(objekteAktivitaeten);
                        } else {
                            this.dbObjektAktivitaeten = (value as ObjektModels.ObjektAktivitaetListElement[]);
                            objekteAktivitaeten.forEach((objAkt) => {
                                const index = this.dbObjektAktivitaeten.findIndex((x) => x.id == objAkt.id);
                                if (index > -1) {
                                    this.dbObjektAktivitaeten[index] = objAkt;
                                } else {
                                    this.dbObjektAktivitaeten.push(objAkt);
                                }
                            });
                        }
                        await this.localStorageService.add('offlineObjektAktivitaeten', this.dbObjektAktivitaeten, this.storageWebMain);
                        resolve(await this.saveObjectActivitiesFilesInDB(objekteAktivitaeten));
                    } else {
                        resolve(true);
                    }
                }, error: (err) => console.error('saveObjectActivitiesInDB(): Fehler bei der Ermittlung der Objektaktivitäten', err),
            });
        });
    }

    public async openLocalDocument(documentID: string, downloadFile: boolean = false, fileName: string = ''): Promise<void> {
        const value = await this.localStorageService.get('offlineObjektAktivitaetenDateien', this.storageWebMain);
        if (value !== false) {
            this.dbObjektAktivitaetenFiles = (value as ObjektModels.ObjektAktivitaetDatei[]);
            if (this.dbObjektAktivitaetenFiles.length > 0) {
                const fileToOpen = this.dbObjektAktivitaetenFiles.find((x) => x.documentID === documentID);
                if (fileToOpen) {
                    const dateiEndung = fileToOpen.fileName.substring(fileToOpen.fileName.lastIndexOf('.'));
                    if (!downloadFile && dateiEndung.toLowerCase() === '.pdf') {
                        // Im PDF-Viewer öffnen
                        const pdfList = this.dbObjektAktivitaeten.filter(objectActivity => objectActivity.imageFile === 'pdf.png').flatMap<DokumentInfo>(pdfActivity => {
                            return {
                                objectID: pdfActivity.objectID,
                                documentID: pdfActivity.id,
                                description: pdfActivity.description,
                                fileName: pdfActivity.documentName,
                                dokumentTyp: DokumentTyp.Notizblatt,
                                dokumentQuelle: DokumentSource.FromWebService,
                                lastModified: new Date()
                            };
                        });
                        this.setPDFDokument(fileToOpen.objectID, fileToOpen.documentID, fileToOpen.fileName, fileToOpen.description, DokumentModels.DokumentTyp.Dokument, DokumentModels.DokumentSource.OfflineObjektNavi, (fileToOpen.lastModified || new Date()), pdfList);
                        this.openPDFViewerDialog();
                    } else {
                        this.getOfflineOtherDokument(fileToOpen.binary, fileName ? fileName : fileToOpen.fileName);
                    }
                }
            }
        }
    }

    // Wenn eine Mutation im PDF-WebViewer gespeichert wurde
    async replaceDokumentInDB(binary: Blob, uploaded: boolean, docInfo = this.dokuInfo) {
        if (docInfo.dokumentTyp == DokumentModels.DokumentTyp.ObjektReport) {
            // Objekt-Report Dokument
            await this.svcObjekte.replaceObjektReportInIndexedDB(docInfo.documentID, binary);
        } else {
            // Service-App Dokument
            const doc = await this.localStorageService.get(docInfo.documentID, this.dbStorageSA_Doku);
            if (doc) {
                const dokumentFile = doc as ServiceAppModels.DokumentDBFile;
                dokumentFile.binary = binary;
                dokumentFile.uploaded = uploaded;
                await this.localStorageService.add(docInfo.documentID, dokumentFile, this.dbStorageSA_Doku);
            }
        }
    }

    private openPDFViewerDialog() {
        this.dialog.open(PDFViewerDialogComponent, {
            maxWidth: '100vw',
            maxHeight: '100vh',
            height: '100%',
            width: '100%',
            data: {isDialogMode: true}
        });
    }

    private findUniquePDFFileName(documentID: string, objectID: string, fileName: string): string {
        // Prüfen, ob in der Indexed-DB nicht schon ein File mit dem gleichen Namen für das gleiche Objekt vorhanden ist.
        let index = this.dbNotizBlaetter.findIndex((x) => x.objectID == objectID && x.documentID !== documentID && x.fileName == fileName);
        if (index >= 0) {
            // Neuen Namen suchen
            let cnt = 1;
            let newFileName = 'Notizblatt_' + cnt + '.pdf';
            let freeFileNameFound = false;
            while (!freeFileNameFound) {
                index = this.dbNotizBlaetter.findIndex((x) => x.objectID == objectID && x.documentID !== documentID && x.fileName == newFileName);
                if (index >= 0) {
                    cnt += 1;
                    newFileName = 'Notizblatt_' + cnt + '.pdf';
                } else {
                    freeFileNameFound = true;
                }
            }
            return newFileName;
        } else {
            return fileName;
        }
    }

    private findUniqueNotizblattFileName(documentID: string, objectID: string, fileName: string): string {
        if (this.dbNotizBlaetter) {
            // Prüfen, ob in der Indexed-DB nicht schon ein File mit dem gleichen Namen für das gleiche Objekt vorhanden ist.
            let index = this.dbNotizBlaetter.findIndex((x) => x.objectID == objectID && x.documentID !== documentID && x.fileName == fileName);
            if (index >= 0) {
                // Neuen Namen suchen
                let cnt = 1;
                let newFileName = 'Notizblatt_' + cnt + '.pdf';
                let freeFileNameFound = false;
                while (!freeFileNameFound) {
                    index = this.dbNotizBlaetter.findIndex((x) => x.objectID == objectID && x.documentID !== documentID && x.fileName == newFileName);
                    if (index >= 0) {
                        cnt += 1;
                        newFileName = 'Notizblatt_' + cnt + '.pdf';
                    } else {
                        freeFileNameFound = true;
                    }
                }
                return newFileName;
            } else {
                return fileName;
            }
        } else {
            return fileName;
        }
    }

    private blobToFile = (theBlob: Blob, fileName: string): File => {
        const b: any = theBlob;
        // A Blob() is almost a File() - it's just missing the two properties below which we will add
        // TODO 2025-12-09 https://swissjira.atlassian.net/browse/EN-4215 LastModifiedDate is deprecated use last modified instead

        b.lastModifiedDate = new Date();
        b.name = fileName;

        // Cast to a File() type
        return theBlob as File;
    };

    private async saveObjectActivitiesFilesInDB(objekteAktivitaeten: ObjektModels.ObjektAktivitaetListElement[]): Promise<boolean> {
        if (objekteAktivitaeten.length > 0) {
            let checkExistingData = true;
            const value = await this.localStorageService.get('offlineObjektAktivitaetenDateien', this.storageWebMain);
            if (value === false) {
                this.dbObjektAktivitaetenFiles = [];
                checkExistingData = false;
            } else {
                this.dbObjektAktivitaetenFiles = (value as ObjektModels.ObjektAktivitaetDatei[]);
            }
            const objekteAktivitaetenDokumente = objekteAktivitaeten.filter((af) => af.art === 1);
            for (const objAkt of objekteAktivitaetenDokumente) {
                if (objAkt.art === 1) {
                    /* Dokument */
                    let fileName = objAkt.documentName;
                    // Wenn es sich um eine s3a-Datei handelt, muss die Dateierweiterung auf pdf geändert werden.
                    // Beispiel: test.s3a wird zu test.pdf
                    const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
                    if (fileExtension.toLowerCase() === '.s3a') {
                        fileName = fileName.substring(0, fileName.lastIndexOf('.')) + '.pdf';
                    }

                    if (fileName) {
                        const document = await this.getOtherDokumentFile(objAkt.id, fileName);
                        if (document) {
                            const objDatei: ObjektModels.ObjektAktivitaetDatei = {
                                documentID: objAkt.id, description: objAkt.description, objectID: objAkt.objectID, fileName, binary: document, lastModified: objAkt.lastModified
                            };
                            if (checkExistingData) {
                                const index = this.dbObjektAktivitaetenFiles.findIndex((x) => x.documentID === objAkt.id);
                                if (index > -1) {
                                    this.dbObjektAktivitaetenFiles[index] = objDatei;
                                } else {
                                    this.dbObjektAktivitaetenFiles.push(objDatei);
                                }
                            } else {
                                this.dbObjektAktivitaetenFiles.push(objDatei);
                            }
                        }
                    }
                }
            }
            await this.localStorageService.add('offlineObjektAktivitaetenDateien', this.dbObjektAktivitaetenFiles, this.storageWebMain);
            return true;
        }
        return true;
    }
}
